/**
 * A project's "Who can read" part: each member, what they read up to, the compartments they hold
 * by name, and how much of the project's memory that lets them see. Clearances and compartments are
 * the organisation's, so this view never edits them; it links to the Memory page's Access view.
 */

import { mayRead, type Entitlement, type ProjectMember } from '@aioe/shared';
import { Eye } from '@gravity-ui/icons';
import { Link } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { useLoad } from '../hooks.ts';
import { LEVEL_TEXT } from './memoryAccessModel.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const ROLE_TEXT: Record<string, string> = { owner: 'Owner', contributor: 'Contributor', viewer: 'Viewer' };

export function ProjectReaders({ projectId, owner, members }: { projectId: string; owner: boolean; members: ProjectMember[] }) {
  const tone = VIEW_TONES.projects!;
  const people = members.filter((m) => m.subjectType === 'user');
  const groupMembers = members.filter((m) => m.subjectType === 'group');
  const graph = useLoad(() => routes.memoryGraph(projectId).catch(() => undefined), [projectId]);
  const names = useLoad(() => routes.memoryCompartments(projectId).then((cs) => new Map(cs.map((c) => [c.id, c.name]))).catch(() => new Map<string, string>()), [projectId]);
  // Owners see every member's entitlement; anyone else sees their own.
  const reach = useLoad(async () => {
    const out = new Map<string, Entitlement>();
    const ids = owner ? people.map((p) => p.subjectId) : [undefined];
    await Promise.all(ids.map((id) => routes.entitlement(projectId, id).then((e) => void out.set(id ?? e.act, e)).catch(() => undefined)));
    return out;
  }, [projectId, owner, people.map((p) => p.subjectId).join(',')]);
  const unused = useLoad(() => (owner && !isCloud() ? routes.unusedGrants(projectId) : Promise.resolve(undefined)), [projectId, owner]);
  const items = (graph.data?.items ?? []).filter((i) => i.kind !== 'edge');
  const total = items.length + (graph.data?.itemsFiltered ?? 0);
  const rows = owner ? people : people.filter((p) => reach.data?.has(p.subjectId));
  const compartment = (id: string) => names.data?.get(id) ?? id;
  const problem = graph.error ?? reach.error;
  return (
    <>
      <ListGroup
        title="Who can read"
        count={rows.length || undefined}
        description={
          <>
            What each member reads up to (the project’s classification, or their own clearance where it is higher), the compartments they hold and how much of the project’s memory that lets them see. Clearances and compartments are the organisation’s, set by administrators on the{' '}
            <Link to="/memory?view=access" className="text-accent hover:underline">
              Memory page
            </Link>
            .
          </>
        }
        empty={rows.length === 0 && groupMembers.length === 0 && !problem ? <Empty compact tone={tone} icon={<Eye />} title="No people on this project" body="Add members under People; what each one reads shows here." /> : undefined}
      >
        {rows.length ? (
          <ListBlock flush>
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th className="hidden w-36 @[40rem]:table-cell">Reads up to</th>
                    <th className="hidden @[48rem]:table-cell">Compartments</th>
                    <th className="data-table__end w-28">Sees</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const e = reach.data?.get(m.subjectId);
                    const sees = e ? items.filter((i) => mayRead(e, i.label)).length : undefined;
                    return (
                      <tr key={m.subjectId}>
                        <td className="[overflow-wrap:anywhere]">
                          <span className="font-medium">{m.name ?? <SubjectName id={m.subjectId} fallback={m.email} />}</span>
                          <span className="data-table__meta">
                            {ROLE_TEXT[m.role] ?? m.role}
                            {e ? <span className="@[40rem]:hidden"> · reads up to {LEVEL_TEXT[e.clearance]}</span> : null}
                          </span>
                        </td>
                        <td className="hidden @[40rem]:table-cell">{e ? LEVEL_TEXT[e.clearance] : '—'}</td>
                        <td className="hidden @[48rem]:table-cell">{e ? (e.compartments.length ? e.compartments.map(compartment).join(', ') : <span className="text-muted">None</span>) : '—'}</td>
                        <td className="data-table__end whitespace-nowrap">{sees === undefined ? '—' : owner ? `${sees.toLocaleString()} of ${total.toLocaleString()}` : sees.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ListBlock>
        ) : null}
        {groupMembers.length ? (
          <ListBlock className="text-xs text-muted">
            Also on the project through {groupMembers.length === 1 ? 'a directory group' : 'directory groups'}:{' '}
            {groupMembers.map((g, i) => (
              <span key={g.subjectId}>
                {i ? ', ' : ''}
                <span className="text-foreground">
                  <SubjectName id={g.subjectId} fallback="a directory group" />
                </span>{' '}
                ({(ROLE_TEXT[g.role] ?? g.role).toLowerCase()})
              </span>
            ))}
            . Each person in a group reads up to their own clearance;{' '}
            <Link to="/memory?view=access" className="text-accent hover:underline">
              check a person
            </Link>{' '}
            on the Memory page.
          </ListBlock>
        ) : null}
        {(graph.data?.itemsFiltered ?? 0) > 0 && owner ? <ListBlock className="text-xs text-muted">Counts are of what you can read; {graph.data!.itemsFiltered.toLocaleString()} more are hidden from you too, so nobody’s count includes them.</ListBlock> : null}
        {problem ? (
          <ListBlock>
            <ErrorNote error={problem} />
          </ListBlock>
        ) : null}
      </ListGroup>
      {owner && unused.data && (
        <ListGroup title="Granted and never read" count={unused.data.unused.length || undefined} description={`Compartments members hold that no one read over ${unused.data.days} days, out of ${unused.data.reads.toLocaleString()} reads.`}>
          {unused.data.unused.length === 0 ? (
            <ListBlock className="text-sm text-muted">Every compartment this project’s members hold was read.</ListBlock>
          ) : (
            unused.data.unused.map((u) => (
              <ListRow
                key={u.compartment}
                title={<span title={u.compartment}>{compartment(u.compartment)}</span>}
                description={
                  <>
                    Held by{' '}
                    {u.grantedTo.map((g, i) => (
                      <span key={`${g.subjectType}/${g.subjectId}`}>
                        {i ? ', ' : ''}
                        {g.subjectType === 'project' ? "the project's members" : <SubjectName id={g.subjectId} fallback={g.subjectType === 'group' ? 'a directory group' : undefined} />}
                      </span>
                    ))}
                  </>
                }
              />
            ))
          )}
        </ListGroup>
      )}
    </>
  );
}
